import { z } from 'zod'
import { AppError } from '@/shared/domain/AppError'
import { contractServicePeriod } from '../../domain/contract'
import type { ContractRecord, ContractStatus, ContractStatusV2 } from '../../domain/contract'

const contractStatusV2Values: ContractStatusV2[] = [
  'DRAFT',
  'PENDING_CLIENT',
  'VIEWED',
  'SIGNED',
  'AWAITING_PAYMENT',
  'ACTIVE',
  'DECLINED',
  'EXPIRED',
  'CANCELLED',
  'COMPLETED',
  'SUSPENDED',
  'TEST',
]

const relatedClientSchema = z.object({
  numero: z.string().nullable(),
  prenom: z.string().nullable(),
  nom: z.string().nullable(),
  entreprise: z.string().nullable(),
  telephone: z.string().nullable().optional(),
  telephone_mobile: z.string().nullable().optional(),
  courriel: z.string().nullable().optional(),
  latitude: z.coerce.number().nullable().optional(),
  longitude: z.coerce.number().nullable().optional(),
})

export const legacyContractRowSchema = z.object({
  id: z.string(),
  numero: z.string().nullable(),
  client_id: z.string(),
  type: z.string().nullable(),
  saison: z.string().nullable(),
  prix: z.coerce.number().nullable(),
  statut: z.enum(['brouillon', 'a_signer', 'en_attente', 'actif', 'suspendu', 'expire', 'annule']),
  date_signature: z.string().nullable(),
  date_debut: z.string().nullable(),
  date_fin: z.string().nullable(),
  renouvellement: z.boolean(),
  latitude: z.coerce.number().nullable().optional(),
  longitude: z.coerce.number().nullable().optional(),
  superficie: z.coerce.number().nullable().optional(),
  adresse_geocodee: z.string().nullable().optional(),
  services: z.array(z.string()).nullable().optional(),
  zone_desservie: z.string().nullable().optional(),
  exclusions: z.string().nullable().optional(),
  obligations_client: z.string().nullable().optional(),
  responsabilites: z.string().nullable().optional(),
  clause_annulation: z.string().nullable().optional(),
  clause_prix: z.string().nullable().optional(),
  clause_execution: z.string().nullable().optional(),
  clause_assurance: z.string().nullable().optional(),
  seuil_declenchement_cm: z.coerce.number().nullable().optional(),
  heure_premier_passage: z.string().nullable().optional(),
  nettoyage_final: z.string().nullable().optional(),
  mode_paiement: z.string().nullable().optional(),
  prix_taxes: z.string().nullable().optional(),
  sans_facturation: z.boolean().nullable().optional(),
  hidden_from_overview: z.boolean().nullable().optional(),
  settings_version_id: z.string().nullable().optional(),
  status_v2: z.string().nullable().optional(),
  lead_id: z.string().nullable().optional(),
  renewed_from_contract_id: z.string().nullable().optional(),
  measurement_mode: z.enum(['NONE', 'SATELLITE']).optional(),
  standard_driveway_capacity: z.coerce.number().int().nullable().optional(),
  sent_at: z.string().nullable().optional(),
  last_sent_at: z.string().nullable().optional(),
  last_viewed_at: z.string().nullable().optional(),
  opened_since_last_send: z.boolean().nullable().optional(),
  submission_type: z.enum(['RESIDENTIAL', 'COMMERCIAL']).nullable().optional(),
  billing_name: z.string().nullable().optional(),
  billing_address_line1: z.string().nullable().optional(),
  billing_address_line2: z.string().nullable().optional(),
  billing_city: z.string().nullable().optional(),
  billing_province: z.string().nullable().optional(),
  billing_postal_code: z.string().nullable().optional(),
  billing_country: z.string().nullable().optional(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
  client: relatedClientSchema.nullable().optional(),
})

export type LegacyContractRow = z.infer<typeof legacyContractRowSchema>

/** Correspondance gelée du registre `docs/migration/status-mappings.md`. */
const statusMap: Record<LegacyContractRow['statut'], ContractStatus> = {
  brouillon: 'DRAFT',
  a_signer: 'PENDING',
  en_attente: 'PENDING',
  actif: 'ACTIVE',
  suspendu: 'SUSPENDED',
  expire: 'EXPIRED',
  annule: 'CANCELLED',
}

function optionalText(value: string | null | undefined): string | undefined {
  const normalized = value?.trim()
  return normalized === '' ? undefined : normalized
}

export function mapLegacyContract(row: LegacyContractRow): ContractRecord {
  // Chaque condition est reprise telle qu'elle est enregistrée; une valeur
  // absente le reste, jamais remplacée par un texte plausible.
  const text = (value: string | null | undefined) => {
    const normalized = value?.trim()
    return normalized === undefined || normalized === '' ? undefined : normalized
  }
  const terms: NonNullable<ContractRecord['terms']> = {
    services: row.services ?? [],
  }
  const conditions: [keyof NonNullable<ContractRecord['terms']>, string | undefined][] = [
    ['servedZone', text(row.zone_desservie)],
    ['exclusions', text(row.exclusions)],
    ['clientObligations', text(row.obligations_client)],
    ['responsibilities', text(row.responsabilites)],
    ['cancellationClause', text(row.clause_annulation)],
    ['priceClause', text(row.clause_prix)],
    ['executionClause', text(row.clause_execution)],
    ['insuranceClause', text(row.clause_assurance)],
    ['firstPassTime', text(row.heure_premier_passage)],
    ['finalCleanup', text(row.nettoyage_final)],
    ['paymentMode', text(row.mode_paiement)],
    ['priceTaxes', text(row.prix_taxes)],
  ]
  for (const [key, value] of conditions) {
    if (value !== undefined) Object.assign(terms, { [key]: value })
  }
  if (row.seuil_declenchement_cm != null) terms.triggerThresholdCm = row.seuil_declenchement_cm

  const serviceAddress = text(row.adresse_geocodee)
  const serviceLatitude = row.latitude ?? row.client?.latitude ?? undefined
  const serviceLongitude = row.longitude ?? row.client?.longitude ?? undefined
  const number = optionalText(row.numero)
  if (!number) {
    throw new AppError('UNEXPECTED_ERROR', 'Un contrat legacy ne possède pas de numéro.', {
      contractId: row.id,
    })
  }
  if (row.prix !== null && (!Number.isFinite(row.prix) || row.prix < 0)) {
    throw new AppError('UNEXPECTED_ERROR', 'Un contrat legacy possède un prix invalide.', {
      contractId: row.id,
    })
  }
  const clientDisplayName =
    optionalText(row.client?.entreprise) ??
    [optionalText(row.client?.prenom), optionalText(row.client?.nom)].filter(Boolean).join(' ')
  const clientNumber = optionalText(row.client?.numero)
  const type = optionalText(row.type)
  const season = optionalText(row.saison)
  const signedOn = optionalText(row.date_signature)
  const startsOn = optionalText(row.date_debut)
  const endsOn = optionalText(row.date_fin)
  // Période absente en base : la saison canonique « AAAA-BBBB » la détermine
  // (1er nov → 1er avr), comme le backfill 20260910010000.
  const servicePeriod = contractServicePeriod({ startsOn, endsOn, season })
  const notes = optionalText(row.notes)
  const clientPhone = optionalText(row.client?.telephone)
  const clientMobilePhone = optionalText(row.client?.telephone_mobile)
  const clientEmail = optionalText(row.client?.courriel)
  const billingLine1 = optionalText(row.billing_address_line1)
  const billingName = optionalText(row.billing_name)
  const billingLine2 = optionalText(row.billing_address_line2)
  const billingCity = optionalText(row.billing_city)
  const billingProvince = optionalText(row.billing_province)
  const billingPostalCode = optionalText(row.billing_postal_code)
  const billingCountry = optionalText(row.billing_country)
  return {
    id: row.id,
    number,
    // Le soft delete vaut archivage, conformément au registre.
    status: row.deleted_at ? 'ARCHIVED' : statusMap[row.statut],
    clientId: row.client_id,
    clientDisplayName: clientDisplayName || 'Client sans nom',
    ...(clientNumber ? { clientNumber } : {}),
    ...(clientPhone ? { clientPhone } : {}),
    ...(clientMobilePhone ? { clientMobilePhone } : {}),
    ...(clientEmail ? { clientEmail } : {}),
    ...(type ? { type } : {}),
    ...(season ? { season } : {}),
    ...(row.prix === null ? {} : { priceCents: Math.round(row.prix * 100) }),
    ...(signedOn ? { signedOn } : {}),
    ...(servicePeriod ? { startsOn: servicePeriod.startsOn, endsOn: servicePeriod.endsOn } : {}),
    renewal: row.renouvellement,
    // L'adresse de service prime; à défaut, la position du Client sert de repère.
    ...(serviceLatitude === undefined || serviceLongitude === undefined
      ? {}
      : { serviceLatitude, serviceLongitude }),
    ...(row.superficie == null ? {} : { surfaceM2: row.superficie }),
    ...(serviceAddress ? { serviceAddress } : {}),
    ...(row.settings_version_id ? { settingsVersionId: row.settings_version_id } : {}),
    terms,
    ...(notes ? { notes } : {}),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ...(row.status_v2 && contractStatusV2Values.includes(row.status_v2 as ContractStatusV2)
      ? { statusV2: row.status_v2 as ContractStatusV2 }
      : {}),
    ...(row.lead_id ? { leadId: row.lead_id } : {}),
    ...(row.renewed_from_contract_id ? { renewedFromContractId: row.renewed_from_contract_id } : {}),
    measurementMode: row.measurement_mode ?? 'NONE',
    ...(row.standard_driveway_capacity == null
      ? {}
      : { standardDrivewayCapacity: row.standard_driveway_capacity }),
    ...(row.sent_at ? { sentAt: row.sent_at } : {}),
    ...(row.last_sent_at ? { lastSentAt: row.last_sent_at } : {}),
    ...(row.last_viewed_at ? { lastViewedAt: row.last_viewed_at } : {}),
    ...(row.opened_since_last_send ? { openedSinceLastSend: true } : {}),
    ...(row.submission_type ? { submissionType: row.submission_type } : {}),
    ...(row.sans_facturation ? { sansFacturation: true } : {}),
    ...(row.hidden_from_overview ? { hiddenFromOverview: true } : {}),
    ...(billingLine1
      ? {
          billingAddress: {
            ...(billingName ? { name: billingName } : {}),
            line1: billingLine1,
            ...(billingLine2 ? { line2: billingLine2 } : {}),
            ...(billingCity ? { city: billingCity } : {}),
            ...(billingProvince ? { province: billingProvince } : {}),
            ...(billingPostalCode ? { postalCode: billingPostalCode } : {}),
            ...(billingCountry ? { country: billingCountry } : {}),
          },
        }
      : {}),
  }
}
